import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { UPDATE_TEAM, DELETE_TEAM, DEACTIVATE_TEAM, ACTIVATE_TEAM, GET_TEAMS } from '../../graphql/teams';
import { 
  validateTeamForm, 
  DEFAULT_TEAM_COLORS,
  TEAM_DEPARTMENT_OPTIONS,
  getAssignedTeamMembers,
  getTeamMemberDisplayName
} from './teams-constants';
import { devLog, devError } from './constants';

export default function EditTeamModal({ isOpen, onClose, team }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: DEFAULT_TEAM_COLORS[0],
    isContractor: false,
    department: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [updateTeam] = useMutation(UPDATE_TEAM, {
    refetchQueries: [{ query: GET_TEAMS }],
    onCompleted: () => {
      devLog('Team updated successfully');
      toast.success(`Team "${formData.name}" updated successfully!`);
      onClose();
    },
    onError: (error) => {
      devError('Error updating team:', error);
      const errorMessage = error.message || 'Failed to update team';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  });

  const [deleteTeam] = useMutation(DELETE_TEAM, {
    refetchQueries: [{ query: GET_TEAMS }],
    onCompleted: () => {
      devLog('Team deleted successfully');
      toast.success(`Team "${team.name}" deleted successfully!`);
      onClose();
    },
    onError: (error) => {
      devError('Error deleting team:', error);
      const errorMessage = error.message || 'Failed to delete team';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  });

  const [deactivateTeam] = useMutation(DEACTIVATE_TEAM, {
    refetchQueries: [{ query: GET_TEAMS }],
    onCompleted: () => {
      devLog('Team deactivated successfully');
      toast.success(`Team "${team.name}" deactivated successfully!`);
      onClose();
    },
    onError: (error) => {
      devError('Error deactivating team:', error);
      const errorMessage = error.message || 'Failed to deactivate team';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  });

  const [activateTeam] = useMutation(ACTIVATE_TEAM, {
    refetchQueries: [{ query: GET_TEAMS }],
    onCompleted: () => {
      devLog('Team activated successfully');
      toast.success(`Team "${team.name}" activated successfully!`);
      onClose();
    },
    onError: (error) => {
      devError('Error activating team:', error);
      const errorMessage = error.message || 'Failed to activate team';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  });

  useEffect(() => {
    if (team) {
      setFormData({
        name: team.name || '',
        description: team.description || '',
        color: team.color || DEFAULT_TEAM_COLORS[0],
        isContractor: team.isContractor || false,
        department: team.department || ''
      });
    }
    setShowDeleteConfirmation(false);
    setError(null);
    setValidationErrors({});
  }, [team]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    // Validate form
    const validation = validateTeamForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    setIsLoading(true);

    try {
      await updateTeam({
        variables: {
          updateTeamInput: {
            id: team.id,
            name: formData.name.trim(),
            description: formData.description?.trim() || null,
            color: formData.color || null,
            isContractor: formData.isContractor,
            department: formData.department || null
          }
        }
      });
    } catch (err) {
      devError('Submit error:', err);
      setError(err.message || 'Failed to update team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await deleteTeam({
        variables: { id: team.id }
      });
    } catch (err) {
      devError('Delete error:', err);
      setError(err.message || 'Failed to delete team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (team.isActive) {
        await deactivateTeam({
          variables: { id: team.id }
        });
      } else {
        await activateTeam({
          variables: { id: team.id }
        });
      }
    } catch (err) {
      devError('Toggle active error:', err);
      setError(err.message || `Failed to ${team.isActive ? 'deactivate' : 'activate'} team`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowDeleteConfirmation(false);
    setError(null);
    setValidationErrors({});
    onClose();
  };

  const assignedMembers = getAssignedTeamMembers(team);

  if (!isOpen || !team) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Edit Team</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={isLoading}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-600 rounded">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Team Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Team Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  maxLength={50}
                  className={`mt-1 block w-full rounded-md border ${
                    validationErrors.name ? 'border-red-500' : 'border-gray-300'
                  } p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500`}
                  placeholder="Enter team name"
                  disabled={isLoading}
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  name="department"
                  id="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                  className={`mt-1 block w-full rounded-md border ${
                    validationErrors.department ? 'border-red-500' : 'border-gray-300'
                  } p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500`}
                  disabled={isLoading}
                >
                  <option value="">Select a department</option>
                  {TEAM_DEPARTMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {validationErrors.department && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.department}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={255}
                  rows={3}
                  className={`mt-1 block w-full rounded-md border ${
                    validationErrors.description ? 'border-red-500' : 'border-gray-300'
                  } p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500`}
                  placeholder="Enter team description (optional)"
                  disabled={isLoading}
                />
                {validationErrors.description && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.description}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.description.length}/255 characters
                </p>
              </div>

              {/* Color */}
              <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700">
                  Team Color
                </label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="color"
                    name="color"
                    id="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="h-10 w-16 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                    disabled={isLoading}
                  />
                  <div className="flex flex-wrap gap-2">
                    {DEFAULT_TEAM_COLORS.slice(0, 5).map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                        className={`w-6 h-6 rounded-full border-2 ${
                          formData.color === color ? 'border-gray-600' : 'border-gray-300'
                        } hover:border-gray-500`}
                        style={{ backgroundColor: color }}
                        disabled={isLoading}
                        title={`Select ${color}`}
                      />
                    ))}
                  </div>
                </div>
                {validationErrors.color && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.color}</p>
                )}
              </div>

              {/* Contractor Checkbox */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isContractor"
                    checked={formData.isContractor}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Contractor Team
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Mark this team as a contractor team for reporting purposes
                </p>
              </div>

              {/* Assigned Members */}
              <div>
                <h4 className="text-sm font-medium text-gray-900">Assigned Members</h4>
                <p className="mt-1 text-xs text-gray-500">
                  Add or remove people from a team member&apos;s profile under Team Assignments.
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {assignedMembers.length > 0 ? (
                    assignedMembers.map((member) => (
                      <span
                        key={member.id || getTeamMemberDisplayName(member)}
                        className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700"
                      >
                        {getTeamMemberDisplayName(member)}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No members assigned to this team</span>
                  )}
                </div>
              </div>

              {/* Team Status */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Team Status</h4>
                    <p className="text-sm text-gray-500">
                      {team.isActive ? 'Active' : 'Inactive'} • {assignedMembers.length} member(s)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleActive}
                    disabled={isLoading}
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      team.isActive
                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    } disabled:opacity-50`}
                  >
                    {team.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Team'}
              </button>
            </div>

            {/* Danger Zone */}
            <div className="mt-8 pt-6 border-t border-red-200">
              <h3 className="text-sm font-medium text-red-900">Danger Zone</h3>
              <p className="mt-1 text-sm text-red-600">
                <strong>Warning:</strong> Permanently delete this team. This action cannot be undone.
                <br />
                <strong>Note:</strong> Teams with members cannot be deleted and should be deactivated instead.
              </p>
              <div className="mt-3">
                {!showDeleteConfirmation ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirmation(true)}
                    className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    disabled={isLoading}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Team Permanently
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Are you absolutely sure?
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>
                            This will permanently delete <strong>{team.name}</strong> and remove all team assignments. 
                            This action cannot be undone.
                          </p>
                        </div>
                        <div className="mt-4 flex space-x-3">
                          <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isLoading}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            {isLoading ? 'Deleting...' : 'Yes, Delete Forever'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowDeleteConfirmation(false)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 